import { rangeTestMbps } from '@/utils/rangeTest';
import { useEffect, useState } from 'react';

type Quality = 'slow-2g'|'2g'|'3g'|'4g'|'unknown';
export type NetInfo = { downlinkMbps: number | null; effectiveType: Quality; measuredMbps?: number | null };

const TEST_URL = process.env.NEXT_PUBLIC_SPEEDTEST_URL
  || 'https://cdn.memvy.com/stories/SEC Championship/memories/1758801298665_sample_1280x720-1080p.mp4';  // set via env in prod

function median(nums: number[]) {
  const a = nums.slice().sort((x,y)=>x-y);
  const m = Math.floor(a.length/2);
  return a.length % 2 ? a[m] : (a[m-1]+a[m])/2;
}

export function useNetworkQuality(): NetInfo {
  const [info, setInfo] = useState<NetInfo>({ downlinkMbps: null, effectiveType: 'unknown' });

  useEffect(() => {
    let cancelled = false;
    const conn: any = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    const hintDown = typeof conn?.downlink === 'number' ? conn.downlink : null;
    const eff: Quality = conn?.effectiveType ?? 'unknown';
    setInfo({ downlinkMbps: hintDown, effectiveType: eff });

    (async () => {
      // 2–3 samples → median
      const samples: number[] = [];
      for (let i = 0; i < 3; i++) {
        const mbps = await rangeTestMbps(TEST_URL, 5 * 1024 * 1024);
        if (mbps) samples.push(mbps);
      }
      if (cancelled) return;
      if (samples.length) {
        const measured = median(samples);
        // conservative combine: take min of hint & measured
        const combined = hintDown ? Math.min(hintDown, measured) : measured;
        setInfo({ downlinkMbps: combined, effectiveType: eff, measuredMbps: measured });
      }
    })();

    const onChange = () => {
      const d = typeof conn?.downlink === 'number' ? conn.downlink : null;
      setInfo(prev => ({ ...prev, downlinkMbps: d ?? prev.downlinkMbps, effectiveType: conn?.effectiveType ?? prev.effectiveType }));
    };
    if (conn?.addEventListener) conn.addEventListener('change', onChange);

    return () => { cancelled = true; if (conn?.removeEventListener) conn.removeEventListener('change', onChange); };
  }, []);

  return info;
}
